import React from 'react';
import Image from 'next/image';
import classnames from 'classnames';
import { Link } from 'components/link/Link';
import { IImage, ILink } from 'utils/types';
import ChevronRight from 'components/assets/chevron-right.svg';
import { ProductImages } from 'components/product-images/ProductImages';

import s from './ProductAlmaject.module.scss';

type Props = {
  tagline?: string;
  title?: React.ReactNode;
  presentation?: React.ReactNode;
  referenceDrug?: React.ReactNode;
  therapeuticClass?: React.ReactNode;
  safetyDataSheet?: ILink;
  barcodes?: ILink;
  boxedWarning?: boolean;
  sidenote?: React.ReactNode;
  itemNumbers?: Array<{
    number?: React.ReactNode;
    strength?: string;
    abc?: string;
    cardinal?: string;
    mckesson?: string;
    md?: string;
  }>;
  images?: Array<{
    image?: IImage;
  }>;
  unitsOfSale?: Array<{
    physicalForm?: string;
    unitOfSale?: string;
    packSize?: string;
    caseQuantity?: string;
    minimumOrder?: string;
  }>;
  variations?: Array<{
    description?: string;
    strength?: string;
    concentration?: string;
    vialSize?: string;
    fillVolume?: string;
    closureSize?: string;
    latexFree?: boolean;
    preservativeFree?: boolean;
  }>;
  additionalInformation?: Array<{
    label?: React.ReactNode;
    value?: React.ReactNode;
  }>;
  tags?: string[];
  children?: React.ReactNode;
};

export const ProductAlmaject = ({
  tagline,
  title,
  presentation,
  referenceDrug,
  therapeuticClass,
  safetyDataSheet,
  barcodes,
  boxedWarning,
  sidenote,
  images,
  itemNumbers,
  unitsOfSale,
  variations,
  additionalInformation,
  tags,
  children,
}: Props) => {
  return (
    <div className={s.product}>
      <div className={s.product__container}>
        <div className={s.product__layout}>
          <div className={s.product__main}>
            <header className={s.product__header}>
              {tagline && <p className={s.product__tagline}>{tagline}</p>}
              {title && <div className={s.product__title}>{title}</div>}
            </header>
            {sidenote && <div className={s.product__note}>{sidenote}</div>}
            <div className={s.product__info}>
              <dl className={s.product__list}>
                <div className={s.product__listItem}>
                  <dt className={s.product__label}>
                    <p>Presentation(s):</p>
                  </dt>
                  <dd className={s.product__value}>{presentation}</dd>
                </div>
                <div className={s.product__listItem}>
                  <dt className={s.product__label}>
                    <p>Reference Listed Drug:</p>
                  </dt>
                  <dd className={s.product__value}>{referenceDrug}</dd>
                </div>
                <div className={s.product__listItem}>
                  <dt className={s.product__label}>
                    <p>Therapeutic Class:</p>
                  </dt>
                  <dd className={s.product__value}>{therapeuticClass}</dd>
                </div>
              </dl>
            </div>
          </div>
          <div className={s.product__aside}>
            <div>
              {images && (
                <ProductImages>
                  {images.map((image) => {
                    if (!image.image) {
                      return null;
                    }

                    return (
                      <Image
                        key={image.image.url}
                        src={image.image.url}
                        width={image.image.width}
                        height={image.image.height}
                        alt={image.image.alt}
                        layout="responsive"
                        objectFit="contain"
                        sizes="960px"
                      />
                    );
                  })}
                </ProductImages>
              )}
            </div>
            <div className={s.product__links}>
              {safetyDataSheet && safetyDataSheet.href && (
                <Link
                  className={s.product__link}
                  href={safetyDataSheet.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Safety Data Sheet
                </Link>
              )}
              {barcodes && barcodes.href && (
                <Link
                  className={s.product__link}
                  href={barcodes.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Barcodes
                </Link>
              )}
            </div>
            <div className={s.product__texts}>
              {boxedWarning && (
                <div className={s.product__warning}>
                  <p>This product contains a BOXED WARNING.</p>
                </div>
              )}
              <div className={s.product__message}>
                <p>For package insert, carton and vial label click on one of the listed NDC's</p>
              </div>
              {/* {!!sidenote && <div className={s.product__sidenote}>{sidenote}</div>} */}
            </div>
          </div>
          <div className={s.product__numbers}>
            <div className={s.product__subtitle}>
              <h3>Wholesale Item Numbers</h3>
            </div>
            <div className={s.table}>
              <table className={s.table__table}>
                <thead className={s.table__head}>
                  <tr className={s.table__row}>
                    <th className={s.table__heading}>Item Number</th>
                    <th className={s.table__heading}>Strength</th>
                    <th className={s.table__heading}>ABC</th>
                    <th className={s.table__heading}>Cardinal</th>
                    <th className={s.table__heading}>McKesson</th>
                    <th className={s.table__heading}>M&D</th>
                  </tr>
                </thead>
                <tbody className={s.table__body}>
                  {itemNumbers &&
                    itemNumbers.map((itemNumber, i) => {
                      return (
                        <tr key={i} className={s.table__row}>
                          <td className={s.table__cell}>{itemNumber.number}</td>
                          <td className={s.table__cell}>{itemNumber.strength}</td>
                          <td className={s.table__cell}>{itemNumber.abc}</td>
                          <td className={s.table__cell}>{itemNumber.cardinal}</td>
                          <td className={s.table__cell}>{itemNumber.mckesson}</td>
                          <td className={s.table__cell}>{itemNumber.md || '-'}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
          <div className={s.product__units}>
            <div className={s.product__subtitle}>
              <h3>Units of Sale</h3>
            </div>
            {unitsOfSale && (
              <div className={s.table}>
                <table className={s.table__table}>
                  <thead className={s.table__head}>
                    <tr className={s.table__row}>
                      <th className={s.table__heading}>Physical Form</th>
                      <th className={s.table__heading}>Unit of Sale</th>
                      <th className={s.table__heading}>Pack Size</th>
                      <th className={s.table__heading}>Minimum Order</th>
                    </tr>
                  </thead>
                  <tbody className={s.table__body}>
                    {unitsOfSale.map((unitOfSale, i) => {
                      return (
                        <tr key={i} className={s.table__row}>
                          <td className={s.table__cell}>{unitOfSale.physicalForm}</td>
                          <td className={s.table__cell}>{unitOfSale.unitOfSale}</td>
                          <td className={s.table__cell}>{unitOfSale.packSize}</td>
                          <td className={s.table__cell}>{unitOfSale.minimumOrder}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className={s.product__variations}>
            <div className={s.product__subtitle}>
              <h3>Variations</h3>
            </div>
            {variations && variations.length > 0 && (
              <div className={s.table}>
                <table className={s.table__table}>
                  <thead className={s.table__head}>
                    <tr className={s.table__row}>
                      <th className={s.table__heading}>Description</th>
                      <th className={s.table__heading}>Strength</th>
                      <th className={s.table__heading}>Concentration</th>
                      <th className={s.table__heading}>Vial Size</th>
                      <th className={s.table__heading}>Fill Volume</th>
                      <th className={s.table__heading}>Closure Size</th>
                      <th className={s.table__heading}>Allergens</th>
                    </tr>
                  </thead>
                  <tbody className={s.table__body}>
                    {variations &&
                      variations.map((variation, i) => {
                        return (
                          <tr key={i} className={s.table__row}>
                            <td className={s.table__cell}>{variation.description}</td>
                            <td className={s.table__cell}>{variation.strength}</td>
                            <td className={s.table__cell}>{variation.concentration}</td>
                            <td className={s.table__cell}>{variation.vialSize}</td>
                            <td className={s.table__cell}>{variation.fillVolume}</td>
                            <td className={s.table__cell}>{variation.closureSize}</td>
                            <td className={s.table__cell}>
                              <div className={s.table__allergens}>
                                {variation.latexFree && (
                                  <img
                                    src={'/static/icons/icon-latex-free.png'}
                                    alt="Latex Free"
                                    width="30"
                                    height="30"
                                  />
                                )}
                                {variation.preservativeFree && (
                                  <img
                                    src={'/static/icons/icon-preservative-free.png'}
                                    alt="Preservative Free"
                                    width="30"
                                    height="30"
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                  <caption className={s.table__caption}>
                    <span>
                      <img
                        src={'/static/icons/icon-latex-free.png'}
                        alt="Latex Free"
                        width="30"
                        height="30"
                      />
                      Latex Free
                    </span>
                    <span>
                      <img
                        src={'/static/icons/icon-preservative-free.png'}
                        alt="Preservative Free"
                        width="30"
                        height="30"
                      />
                      Preservative Free
                    </span>
                  </caption>
                </table>
              </div>
            )}
          </div>
          <div className={s.product__additional}>
            <div className={s.product__subtitle}>
              <h3>Additional Information</h3>
            </div>
            {additionalInformation && (
              <dl className={s.product__list}>
                {additionalInformation.map((item, i) => {
                  if (!item.label || !item.value) {
                    return null;
                  }

                  return (
                    <div className={s.product__listItem} key={i}>
                      <dt className={s.product__label}>{item.label}</dt>
                      <dd className={s.product__value}>{item.value}</dd>
                    </div>
                  );
                })}
              </dl>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
